import React, { useEffect, useMemo, useState } from "react";


import {
  Alert,
  Button,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Add, FilterList, ImportExport, RestartAlt } from "@mui/icons-material";

import {
  ButtonIcon,
  CreateNewCard,
  FilterBox,
  SearchBox,
  ViewToggle,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { AudienceCardView, AudienceListView } from "@/components/molecules";
import { useDemo, useSnackbar, useViewModeContext } from "@/context";
import { useAudienceLinks, useAudiences } from "@/hooks";
import { Audience, FilterOption } from "@/types";
import { audienceSortOptions, demoClients, filterAudiences, uploadModeOptions } from "@/utils";
import { UploadSkeleton } from "./UploadSkeleton";


const UploadListPage: React.FC = () => {
  const links = useAudienceLinks();
  const { openSnackbar } = useSnackbar();
  const { isDemoMode, demoClient } = useDemo();

  const { audiences, loading, error: audiencesError } = useAudiences("upload");

  const [rows, setRows] = useState<Audience[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("");
  const [projectFilter, setProjectFilter] = useState<string>("");
  const [modeFilter, setModeFilter] = useState<string>("");
  const [filterBy, setFilterBy] = useState<string>("");
  const [projects, setProjects] = useState<FilterOption[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { viewMode, handleViewModeChange } = useViewModeContext();
  const filterApplied = searchText !== "" || projectFilter !== ""|| modeFilter !== "" || sortBy !== "";

  const listState = useMemo(() => ({
    filterApplied,
    pagination: { page: currentPage, setPage: setCurrentPage },
  }), [filterApplied, currentPage, setCurrentPage])

  useEffect(() => {
    if (isDemoMode && demoClient) {
      setProjectFilter(demoClients.find(x => x.name === demoClient)?.label || "");
    }
  }, [isDemoMode, demoClient]);

  useEffect(() => {
    if (isDemoMode) {
      setProjects(
        demoClients.filter(x => x.name === demoClient).map(x => ({ name: x.label, label: x.label }))
      );
    } else {
      setProjects(
        Array.from(
          new Set(
            audiences
              ?.filter((a) => a.project_name !== null)
              .map((a) => a.project_name as string)
              .sort((a, b) => a.localeCompare(b))
          )
        ).map((x) => ({ name: x, label: x }))
      );
    }

    const filteredAudiences = filterAudiences(
      audiences,
      searchText,
      projectFilter,
      sortBy,
      modeFilter,
      'upload_mode'
    );

    if (filteredAudiences) setRows(filteredAudiences);
  }, [audiences, searchText, projectFilter, modeFilter, sortBy, isDemoMode, demoClient]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");

  const handleSortSelect = (event: SelectChangeEvent<string | string[]>) =>
    setSortBy(event.target.value as string);

  const handleProjectSelect = (value: string) =>
    setProjectFilter((prevState) => (prevState === value ? "" : value));

  const handleModeSelect = (value: string) =>
    setModeFilter((prevState) => (prevState === value ? "" : value));

  const handleReset = () => {
    setSearchText("");
    setProjectFilter("");
    setModeFilter("");
    setSortBy("");
    setCurrentPage(1);
  };

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<Add />}
        onClick={links.create}
      >
        Create New
      </Button>
    </Stack>
  );

  return (
    <PageContainer actions={actionButtons}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography>My Uploads</Typography>
        </Grid>

        {/* Filters & Search bar */}
        <Grid container size={12} alignItems="center">
          <Grid>
            <SearchBox value={searchText} onSearch={handleSearch} />
          </Grid>
          <Grid>
            <FilterBox
              label="Project"
              value={projectFilter}
              options={projects}
              onClick={handleProjectSelect}
              startIcon={<FilterList fontSize="small" />}
              showSelected
              disabled={isDemoMode}
            />
          </Grid>
          <Grid>
            <FilterBox
              label="Upload Mode"
              value={modeFilter}
              options={uploadModeOptions.map(x => ({name: x.value, label: x.label, icon: x.icon}))}
              onClick={handleModeSelect}
              startIcon={<FilterList fontSize="small" />}
              showSelected
            />
          </Grid>
          <Grid>
            <FilterBox
              label="Sort"
              value={sortBy}
              options={audienceSortOptions}
              onChange={handleSortSelect}
              startIcon={<ImportExport fontSize="small" />}
              showSelected
            />
          </Grid>
          <Grid>
            <ViewToggle
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
            />
          </Grid>
          <Grid>
            <ButtonIcon
              onClick={handleReset}
              color="info"
              size={38}
              Icon={RestartAlt}
              tooltip="Reset Filters"
            />
          </Grid>
        </Grid>

        {/* Main Content */}
        <Grid container size={12} mb={4}>
          {loading ? (
            <UploadSkeleton />
          ) : audiencesError ? (
            <Grid>
              <Alert variant="filled" severity="error">
                It was not possible to load your uploads at this time, please
                try again.
              </Alert>
            </Grid>
          ) : rows.length === 0 ? (
            !filterApplied && (
              // Create Audience Card - Only show if no filter has been applied
              <CreateNewCard
                title="Upload New File"
                onClick={links.create}
              />
            )
          ) : viewMode === "list" ? (
            // List View
            <AudienceListView
              audiences={rows}
              listState={listState}
            />
          ) : (
            // Card View
            <AudienceCardView
              audiences={rows}
              listState={listState}
            />
          )}
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default UploadListPage;
